import React from 'react';
import AntdIcon from '@ant-design/icons';
import styled from 'styled-components';
import { featureEnabled } from 'utils';
import { Color } from '@bb/elements-web-rebranding';
import theme, { ColorNameType } from '../theme';
import iconLibrary from './iconLibrary';
import { IconNameType } from './types';

export type IconProps = {
  color?: ColorNameType | null;
  className?: string;
  style?: React.CSSProperties;
  type: IconNameType;
  width?: number | string;
  height?: number | string;
  offset?: [number, number];
  flipped?: boolean;
  onClick?: () => void;
};

export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  (
    {
      color,
      className = '',
      style = {},
      type,
      width,
      height,
      offset,
      flipped,
      onClick,
    },
    ref,
  ) => {
    const Component = iconLibrary[type];

    if (!Component) {
      throw new Error(
        `Please provide a valid type prop for the Icon component. Provided: ${type}`,
      );
    }

    const getWidth = () => (typeof width === 'number' ? `${width}px` : width);

    const getHeight = () => {
      if (height) return typeof height === 'number' ? `${height}px` : height;
      if (!width) return undefined;
      if (typeof width === 'number') return `${width}px`;
      if (width.indexOf('%') === -1) return width;
      return undefined;
    };

    return (
      <StyledIcon
        ref={ref}
        component={Component}
        className={className}
        style={style}
        $width={getWidth()}
        $height={getHeight()}
        $offset={offset}
        $flipped={flipped}
        fill={
          featureEnabled('supraRebrandingFeature', 1) &&
          color &&
          ['primary', 'blue7'].includes(color)
            ? Color.PRIMARY
            : theme.getColor(color)
        }
        onClick={onClick}
      />
    );
  },
);

const StyledIcon = styled(AntdIcon)<{
  $width?: string;
  $height?: string;
  $offset?: [number, number];
  $flipped?: boolean;
  $color?: string;
  fill: string;
}>`
  svg {
    width: ${theme.iconSize};
    height: ${theme.iconSize};
    ${({ $color }) => $color && `color: ${$color}`};
  }

  && {
    ${({ $flipped }) => `transform: scaleY(${$flipped ? -1 : 1});`}
    transition: all 1s;
  }

  &&& {
    line-height: 0;
    ${({ fill }) => fill && `color: ${fill}`};
    svg {
      ${({ fill }) => fill && `fill: ${fill}`};
      ${({ $width }) => $width && `width: ${$width}`};
      ${({ $height }) => $height && `height: ${$height}`};
    }
    ${({ $offset }) => {
      if ($offset) {
        return `
          position: relative;
          left: ${$offset[0]}px;
          top: ${$offset[1]}px;
        `;
      }
      return ``;
    }}
  }
`;

export { IconNameType };
export default styled(Icon)``;
